import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Check,
  ChevronDown,
  ChevronRight,
  Euro,
  Trash2,
  Undo2,
} from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { CommissionPill } from "@/components/ui/CommissionPill";
import { AvanceTag } from "@/components/ui/AvanceTag";
import { isCommissionInAdvance, isSaleInAdvance } from "@shared/advance";
import { api, type SaleWithRelations } from "@/lib/api";
import { usePeriod } from "@/lib/period";
import { useToast } from "@/components/ui/Toast";
import {
  cn,
  formatDate,
  formatEUR,
  fullName,
  initials,
  todayISO,
} from "@/lib/utils";
import { PRODUCTS } from "@shared/products";
import type { CommissionStatus } from "@shared/schema";
import { COMMISSION_TEXT_COLOR, TONE_STYLES } from "@/lib/colors";

type Filter = "all" | "pending" | "due" | "paid";

// Aggregate a sale's commission state across its installments:
// fully paid → paid; any due → due; otherwise still waiting (rétractation).
function aggregateCommissionStatus(
  statuses: CommissionStatus[],
): CommissionStatus {
  if (statuses.length > 0 && statuses.every((s) => s === "paye")) return "paye";
  if (statuses.some((s) => s === "du")) return "du";
  if (statuses.some((s) => s === "en_attente_retractation"))
    return "en_attente_retractation";
  return statuses[0] ?? "en_attente_retractation";
}


export function SalesPage() {
  const [sales, setSales] = useState<SaleWithRelations[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<Filter>("all");
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const toast = useToast();
  const { inPeriod } = usePeriod();

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { sales } = await api.listSales();
      setSales(sales);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erreur");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Ventes de la période (par date de close).
  const scoped = useMemo(
    () => sales.filter((s) => inPeriod(s.closedAt)),
    [sales, inPeriod],
  );

  const filtered = useMemo(() => {
    if (filter === "all") return scoped;
    return scoped
      .map((s) => ({
        ...s,
        payments: s.payments.filter((p) => {
          if (filter === "pending")
            return p.effectiveCommissionStatus === "en_attente_retractation";
          if (filter === "due") return p.effectiveCommissionStatus === "du";
          if (filter === "paid") return p.effectiveCommissionStatus === "paye";
          return true;
        }),
      }))
      .filter((s) => s.payments.length > 0);
  }, [scoped, filter]);

  const totals = useMemo(() => {
    const t = { pending: 0, due: 0, paid: 0, sales: 0 };
    for (const s of scoped) {
      t.sales += s.totalAmount;
      for (const p of s.payments) {
        if (p.effectiveCommissionStatus === "en_attente_retractation")
          t.pending += p.commissionAmount;
        if (p.effectiveCommissionStatus === "du") t.due += p.commissionAmount;
        if (p.effectiveCommissionStatus === "paye") t.paid += p.commissionAmount;
      }
    }
    return t;
  }, [scoped]);

  function toggle(saleId: number) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(saleId)) next.delete(saleId);
      else next.add(saleId);
      return next;
    });
  }

  async function markPaymentPaid(paymentId: number, status: "due" | "paye") {
    try {
      await api.updatePayment(paymentId, {
        status,
        paidDate: status === "paye" ? todayISO() : null,
      });
      toast.success(status === "paye" ? "Paiement marqué payé" : "Annulé");
      refresh();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erreur");
    }
  }

  async function deleteSale(saleId: number, clientName: string) {
    if (
      !confirm(
        `Supprimer la vente de ${clientName} ? Cette action supprime aussi tous les paiements et commissions associés.`,
      )
    )
      return;
    try {
      await fetch(`/api/sales/${saleId}`, {
        method: "DELETE",
        credentials: "same-origin",
      });
      toast.success("Vente supprimée");
      refresh();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erreur");
    }
  }

  async function markCommissionPaid(
    paymentId: number,
    next: CommissionStatus,
  ) {
    try {
      await api.updatePayment(paymentId, {
        commissionStatus: next,
        commissionPaidDate: next === "paye" ? todayISO() : null,
      });
      toast.success("Commission mise à jour");
      refresh();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erreur");
    }
  }

  return (
    <>
      <PageHeader
        title="Ventes & commissions"
        subtitle="Échéancier complet, état de chaque commission"
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <SummaryCard
          tone="navy"
          label="Volume ventes"
          value={formatEUR(totals.sales)}
        />
        <SummaryCard
          tone="yellow"
          label="Comm. en attente rétractation"
          value={formatEUR(totals.pending)}
        />
        <SummaryCard
          tone="coral"
          label="Comm. dues"
          value={formatEUR(totals.due)}
        />
        <SummaryCard
          tone="green"
          label="Comm. encaissées"
          value={formatEUR(totals.paid)}
        />
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        {(
          [
            ["all", "Toutes"],
            ["pending", "Attente rétractation"],
            ["due", "Dues"],
            ["paid", "Encaissées"],
          ] as Array<[Filter, string]>
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setFilter(key)}
            className={cn(
              "btn btn-sm",
              filter === key ? "btn-primary" : "btn-outline",
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="surface p-8 text-center text-sm text-[var(--color-muted-foreground)]">
          Chargement…
        </div>
      ) : filtered.length === 0 ? (
        <div className="surface p-10 text-center">
          <p className="text-[var(--color-muted-foreground)]">
            Aucune vente à afficher.
          </p>
        </div>
      ) : (
        <div className="surface overflow-x-auto">
          <table className="table-base">
            <thead>
              <tr>
                <th></th>
                <th>Client</th>
                <th>Produit</th>
                <th>Close</th>
                <th>Plan</th>
                <th className="text-right">Montant</th>
                <th className="text-right">Commission</th>
                <th>Échéancier</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => {
                const isExpanded = expanded.has(s.id);
                const paidCount = s.payments.filter(
                  (p) => p.status === "paye",
                ).length;
                return (
                  <>
                    <tr
                      key={`row-${s.id}`}
                      onClick={() => toggle(s.id)}
                      className="cursor-pointer"
                    >
                      <td className="w-8">
                        {isExpanded ? (
                          <ChevronDown size={16} />
                        ) : (
                          <ChevronRight size={16} />
                        )}
                      </td>
                      <td>
                        {s.prospect ? (
                          <Link
                            to={`/prospects/${s.prospect.id}`}
                            className="flex items-center gap-2.5 min-w-0"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="avatar w-8 h-8 text-xs">
                              {initials(fullName(s.prospect))}
                            </div>
                            <span className="font-medium text-[var(--color-navy-700)] hover:text-[var(--color-coral-600)] truncate">
                              {fullName(s.prospect)}
                            </span>
                          </Link>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="text-sm">{PRODUCTS[s.product].label}</td>
                      <td className="text-sm tabular">
                        {formatDate(s.closedAt)}
                      </td>
                      <td className="text-sm tabular">
                        {s.paymentPlan === 1 ? "1x" : `${s.paymentPlan}x`}
                      </td>
                      <td className="text-right font-semibold tabular">
                        {formatEUR(s.totalAmount)}
                      </td>
                      <td
                        className={cn(
                          "text-right tabular font-semibold",
                          COMMISSION_TEXT_COLOR[
                            aggregateCommissionStatus(
                              s.payments.map(
                                (p) => p.effectiveCommissionStatus,
                              ),
                            )
                          ],
                        )}
                      >
                        {formatEUR(s.totalAmount * s.commissionRate)}
                      </td>
                      <td>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-[var(--color-muted-foreground)] tabular">
                            {paidCount}/{s.payments.length} payés · rétractation{" "}
                            {formatDate(s.retractationEndsAt)}
                          </span>
                          {isSaleInAdvance(
                            s.payments.map((p) => p.effectiveCommissionStatus),
                            s.retractationEndsAt,
                            todayISO(),
                          ) && <AvanceTag />}
                        </div>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr key={`exp-${s.id}`} className="bg-[var(--color-surface-2)]">
                        <td></td>
                        <td colSpan={7} className="!py-4">
                          <div className="rounded-[var(--radius-md)] border border-[var(--color-border)] bg-white overflow-hidden">
                            <table className="table-base">
                              <thead>
                                <tr>
                                  <th>#</th>
                                  <th>Échéance</th>
                                  <th className="text-right">Montant</th>
                                  <th>Statut paiement</th>
                                  <th className="text-right">Commission</th>
                                  <th>Statut commission</th>
                                  <th></th>
                                </tr>
                              </thead>
                              <tbody>
                                {s.payments.map((p) => (
                                  <tr key={p.id}>
                                    <td className="font-mono text-xs text-[var(--color-muted-foreground)]">
                                      {p.installmentIndex}
                                    </td>
                                    <td className="text-sm tabular">
                                      {formatDate(p.dueDate)}
                                    </td>
                                    <td className="text-right font-medium tabular">
                                      {formatEUR(p.amount)}
                                    </td>
                                    <td>
                                      {p.status === "paye" ? (
                                        <span className="pill bg-[color-mix(in_srgb,#16a34a_12%,white)] text-[#0f6b30]">
                                          <span className="pill-dot bg-[#16a34a]" />
                                          Payé {formatDate(p.paidDate)}
                                        </span>
                                      ) : (
                                        <span className="pill bg-[var(--color-muted)] text-[var(--color-muted-foreground)]">
                                          <span className="pill-dot bg-[var(--color-muted-foreground)]" />
                                          À échoir
                                        </span>
                                      )}
                                    </td>
                                    <td
                                      className={cn(
                                        "text-right font-semibold tabular",
                                        COMMISSION_TEXT_COLOR[
                                          p.effectiveCommissionStatus
                                        ],
                                      )}
                                    >
                                      {formatEUR(p.commissionAmount)}
                                    </td>
                                    <td>
                                      <div className="flex items-center gap-1.5">
                                        <CommissionPill
                                          status={p.effectiveCommissionStatus}
                                        />
                                        {isCommissionInAdvance(
                                          p.effectiveCommissionStatus,
                                          s.retractationEndsAt,
                                          todayISO(),
                                        ) && <AvanceTag />}
                                      </div>
                                    </td>
                                    <td>
                                      <div className="flex items-center justify-end gap-1.5">
                                        {p.status === "due" ? (
                                          <button
                                            type="button"
                                            className="btn btn-outline btn-sm"
                                            onClick={() =>
                                              markPaymentPaid(p.id, "paye")
                                            }
                                          >
                                            <Check size={13} /> Vente effectuée
                                          </button>
                                        ) : (
                                          <button
                                            type="button"
                                            className="btn btn-ghost btn-sm"
                                            onClick={() =>
                                              markPaymentPaid(p.id, "due")
                                            }
                                          >
                                            <Undo2 size={13} />
                                          </button>
                                        )}
                                        {(p.effectiveCommissionStatus === "du" ||
                                          (p.effectiveCommissionStatus ===
                                            "en_attente_retractation" &&
                                            p.status === "paye")) && (
                                          <button
                                            type="button"
                                            className="btn btn-success btn-sm"
                                            onClick={() =>
                                              markCommissionPaid(p.id, "paye")
                                            }
                                            title={
                                              p.effectiveCommissionStatus ===
                                              "en_attente_retractation"
                                                ? "Encaisser en avance (avant fin de rétractation)"
                                                : undefined
                                            }
                                          >
                                            <Euro size={13} /> Comm. encaissée
                                          </button>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          {s.notes && (
                            <p className="text-xs text-[var(--color-muted-foreground)] mt-3 italic">
                              {s.notes}
                            </p>
                          )}
                          <div className="flex justify-end mt-3">
                            <button
                              type="button"
                              className="btn btn-danger btn-sm"
                              onClick={() =>
                                deleteSale(s.id, s.prospect ? fullName(s.prospect) : "ce client")
                              }
                            >
                              <Trash2 size={13} /> Supprimer la vente
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function SummaryCard({
  tone,
  label,
  value,
}: {
  tone: "navy" | "coral" | "yellow" | "green";
  label: string;
  value: string;
}) {
  const accent = TONE_STYLES[tone].border;
  return (
    <div className={cn("surface p-4 border-l-4", accent)}>
      <div className="text-xs uppercase tracking-wide text-[var(--color-muted-foreground)] font-medium">
        {label}
      </div>
      <div className="text-xl font-bold tabular text-[var(--color-navy-700)] mt-1">
        {value}
      </div>
    </div>
  );
}
